import  { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box,
  Typography, 
  Fade, 
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Settings as SettingsIcon
} from "@mui/icons-material";
import JobCodeButtonGrid from './JobCodeButtonGrid';
import axios from "axios";
import PropTypes from 'prop-types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function SetupTimes(props) {
    const [jobCodes, setJobCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fadeIn, setFadeIn] = useState(false);
    
    const { 
        username, 
        jobNumber, 
        customer, 
        operation, 
        product, 
        startedTime, 
        start_datetime, 
        employee_id, 
        machine_code, 
        operation_code, 
        operation_description 
    } = props;

    const operationType = "Setup";

    const navigate = useNavigate();

    useEffect(() => {
        let redirectTimer;

        const fetchJobCodes = async () => {
            try {
                setLoading(true);
                setError('');
                const res = await axios.get(`${API_BASE_URL}/SetupTimes/${username}`);
                setJobCodes(res.data);
                
                // Trigger fade-in animation after data loads
                setTimeout(() => setFadeIn(true), 100);
            } catch (err) {
                console.error("Error fetching setup times:", err);

                // Session-loss detection. Backend doesn't reliably use
                // 401/403 for session issues, so we also catch:
                //   - 404 (user/session record gone)
                //   - "no session"-style error messages (admin cleared)
                // Other errors keep the existing Alert behavior so
                // transient failures don't disrupt an active job.
                const status = err?.response?.status;
                const message = (
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    ""
                ).toLowerCase();

                const isAuthError = status === 401 || status === 403;
                const isNotFound = status === 404;
                const isNoSessionMessage =
                    message.includes("no active") ||
                    message.includes("no ongoing") ||
                    message.includes("not found") ||
                    message.includes("no session") ||
                    message.includes("session not found");

                if (isAuthError || isNotFound || isNoSessionMessage) {
                    setError('Session expired. Redirecting to login...');
                    redirectTimer = setTimeout(() => {
                        navigate("/", { replace: true });
                    }, 2500);
                } else {
                    setError('Failed to load setup operations. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };
        
        if (username) {
            fetchJobCodes();
        }

        return () => {
            if (redirectTimer) clearTimeout(redirectTimer);
        };
    }, [username, navigate]);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '260px',
                flexDirection: 'column',
                gap: 2
            }}>
                <CircularProgress 
                    size={60} 
                    thickness={4}
                    sx={{ 
                        color: '#9c27b0',
                        filter: 'drop-shadow(0 4px 8px rgba(156, 39, 176, 0.3))'
                    }} 
                />
                <Typography variant="body1" color="textSecondary" fontWeight={500}>
                    Loading setup operations...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '260px',
                p: 2
            }}>
                <Alert 
                    severity="error" 
                    sx={{ 
                        borderRadius: '12px',
                        maxWidth: 400
                    }}
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Fade in={fadeIn} timeout={800}>

                        {jobCodes.length > 0 ? (
                            <Box sx={{ width: '100%' }}>
                                <JobCodeButtonGrid 
                                    jobCodes={jobCodes} 
                                    username={username} 
                                    jobNumber={jobNumber} 
                                    customer={customer} 
                                    operation={operation} 
                                    product={product} 
                                    operationType={operationType} 
                                    startedTime={startedTime} 
                                    employee_id={employee_id} 
                                    machine_code={machine_code} 
                                    start_datetime={start_datetime} 
                                    operation_code={operation_code} 
                                    operation_description={operation_description}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ 
                                textAlign: 'center',
                                p: 4
                            }}>
                                <SettingsIcon 
                                    sx={{ 
                                        fontSize: 64, 
                                        color: 'rgba(156, 39, 176, 0.3)',
                                        mb: 2
                                    }} 
                                />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    No Setup Operations Available
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    There are currently no setup operations configured for this job.
                                </Typography>
                            </Box>
                        )}



 
        </Fade>
    );
}

SetupTimes.propTypes = {
    username: PropTypes.string.isRequired,
    jobNumber: PropTypes.string.isRequired,
    customer: PropTypes.string.isRequired,
    operation: PropTypes.string.isRequired,
    product: PropTypes.string.isRequired,
    startedTime: PropTypes.string.isRequired,
    start_datetime: PropTypes.string.isRequired,
    employee_id: PropTypes.string.isRequired,
    machine_code: PropTypes.string.isRequired,
    operation_code: PropTypes.string.isRequired,
    operation_description: PropTypes.string.isRequired,
};